import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * CAC registrations customers have paid PayIQ to file.
 *
 * This is the working queue for whoever actually submits them, which is why
 * the detail carries the proposed names, nature of business, address and
 * proprietor: those are the fields that go on the CAC form. The app used to
 * collect all of them into React state and throw them away.
 */

export type RegistrationPackageKey = 'business_name' | 'limited_company';

export type RegistrationStatus =
  | 'awaiting_payment'
  | 'paid'
  | 'filing'
  | 'registered'
  | 'rejected'
  | 'cancelled';

export interface BusinessRegistration {
  id: string;
  userId: string;
  businessId: string | null;
  packageKey: RegistrationPackageKey;
  /** Kobo, as quoted when the filing was opened. Doesn't move if prices do. */
  feeKobo: string;
  proposedName1: string;
  proposedName2: string;
  natureOfBusiness: string | null;
  registeredAddress: string | null;
  proprietorName: string | null;
  proprietorEmail: string | null;
  proprietorPhone: string | null;
  status: RegistrationStatus;
  amountPaidKobo: string | null;
  paidAt: string | null;
  approvedName: string | null;
  rcNumber: string | null;
  registeredOn: string | null;
  /** What the filing produced, once a reviewer has attached it. */
  documents: { name: string; url: string }[];
  /** When those were emailed out. Set once, so a later correction does not
   *  re-send somebody's certificate. */
  documentsSentAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegistrationPackagePrice {
  key: RegistrationPackageKey;
  /** Naira. */
  fee: number;
  active: boolean;
  updatedAt: string;
}

export interface ListRegistrationsParams {
  status?: RegistrationStatus;
  packageKey?: RegistrationPackageKey;
  search?: string;
  page?: number;
  limit?: number;
}

export const businessRegistrationsApi = {
  list: async (params: ListRegistrationsParams = {}) => {
    const { data } = await apiClient.get<
      ApiResponse<{
        items: BusinessRegistration[];
        total: number;
        page: number;
        limit: number;
      }>
    >('/admin/business-registrations', { params });
    return data.data;
  },

  get: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<BusinessRegistration>>(
      `/admin/business-registrations/${id}`,
    );
    return data.data;
  },

  updateStatus: async (
    id: string,
    input: {
      status: RegistrationStatus;
      approvedName?: string;
      rcNumber?: string;
      registeredOn?: string;
      rejectionReason?: string;
      /** What the filing produced. Attached to the email announcing it. */
      documents?: { name: string; url: string }[];
    },
  ) => {
    const { data } = await apiClient.patch<ApiResponse<BusinessRegistration>>(
      `/admin/business-registrations/${id}/status`,
      input,
    );
    return data.data;
  },

  /**
   * Put one document in storage and get its URL back.
   *
   * One at a time, and separate from marking the registration. A certificate
   * is megabytes and the status change is a small write — pushing files
   * through that call would make the action that matters most here the
   * slowest and likeliest to time out, and a timeout there is a customer
   * told nothing about a filing that finished.
   */
  uploadDocument: async (
    id: string,
    name: string,
    file: string,
  ): Promise<{ name: string; url: string }> => {
    const { data } = await apiClient.post<
      ApiResponse<{ name: string; url: string }>
    >(`/admin/business-registrations/${id}/documents`, { name, file });
    return data.data!;
  },

  packages: async () => {
    const { data } = await apiClient.get<ApiResponse<RegistrationPackagePrice[]>>(
      '/admin/business-registrations/packages/all',
    );
    return data.data;
  },

  /** New filings only — an open one keeps the fee it was quoted. */
  setPrice: async (
    key: RegistrationPackageKey,
    input: { fee: number; active?: boolean },
  ) => {
    const { data } = await apiClient.patch<ApiResponse<RegistrationPackagePrice>>(
      `/admin/business-registrations/packages/${key}`,
      input,
    );
    return data.data;
  },
};
